<script setup lang="ts">
  import logger from '@leaflink/snitch';
  import DOMPurify from 'dompurify';
  import capitalize from 'lodash-es/capitalize';
  import { computed, isVNode, RenderFunction, useCssModule } from 'vue';

  import { StatusIcon, StatusIcons, StatusSeverities, StatusSeverity } from '../../../types/statusLevels';
  import CustomRender from '../CustomRender/CustomRender.vue';
  import Icon from '../Icon/Icon.vue';

  export interface ToastProps {
    /**
     * The toast text.
     */
    text: string | RenderFunction;
    /**
     * The toast status.
     */
    status?: StatusSeverity;
    /**
     * The container distance from the top of the viewport.
     */
    offsetTop?: string;
  }

  const classes = useCssModule();

  const props = withDefaults(defineProps<ToastProps>(), {
    offsetTop: undefined,
    status: 'info',
  });

  const statusIcon = computed<StatusIcon>(() =>
    props.status ? StatusIcons[capitalize(props.status)] : StatusIcons.Info,
  );

  const ariaRole = computed<string>(() => {
    if (props.status === StatusSeverities.Error) {
      return 'alert';
    } else {
      return 'status';
    }
  });

  const renderContentWithRenderFn = () => {
    if (typeof props.text !== 'function') {
      logger.error(
        `Toast: ${props.text} is not a valid text prop value. Must be either a render function or a string.`,
      );
      return null;
    }

    const content = (props.text as RenderFunction)();

    // If, for some reason, the function passed it's not a render function (h)
    // and/or does not return a VNode
    if (!isVNode(content)) {
      logger.error('Toast: The text prop must return a VNode.');
      return null;
    }

    return content;
  };
</script>

<template>
  <li
    :role="ariaRole"
    :class="[classes.root, classes[`status--${props.status}`], { [classes['custom-offset-top']]: props.offsetTop }]"
    class="stash-toast"
    data-test="stash-toast"
  >
    <Icon :name="statusIcon" />
    <!-- eslint-disable-next-line vue/no-v-html -->
    <div v-if="typeof props.text === 'string'" v-html="DOMPurify.sanitize(text as string)"></div>
    <div v-else><CustomRender :render="renderContentWithRenderFn" /></div>
  </li>
</template>

<style module>
  @reference "../../../styles/main.css";

  @layer utilities {
    .root {
      display: flex;
      align-items: center;
      width: 100%;
      border-width: 1px --spacing(1.5) 1px 1px;
      border-radius: var(--radius-sm);
      padding: calc(--spacing(1.5) * 3) --spacing(3);
      cursor: pointer;

      &:first-child {
        margin-block-start: calc(var(--height-topbar) + --spacing(6));
      }

      /* Override the default offset */
      &.custom-offset-top:first-child {
        margin-block-start: v-bind(props.offsetTop);
      }
    }

    .root > * + * {
      margin-left: --spacing(3);
    }

    .root div {
      margin-bottom: 0;
      color: var(--color-ice-900);
      font-size: 1rem;
    }

    .status--success {
      background-color: var(--color-green-100);
      border-color: var(--color-green-500);
    }

    .status--success svg {
      color: var(--color-green-500);
    }

    .status--error {
      background-color: var(--color-red-100);
      border-color: var(--color-red-500);
    }

    .status--error svg {
      color: var(--color-red-500);
    }

    .status--info {
      background-color: var(--color-blue-100);
      border-color: var(--color-blue-500);
    }

    .status--info svg {
      color: var(--color-blue-500);
    }

    .status--warning {
      background-color: var(--color-orange-100);
      border-color: var(--color-orange-500);
    }

    .status--warning svg {
      color: var(--color-orange-500);
    }
  }
</style>
